<template>
  <div class="voice-step">
    <el-card shadow="never">
      <template #header>
        <div class="card-header">
          <span>语音合成</span>
          <div class="header-actions">
            <el-select v-model="defaultSpeaker" placeholder="选择默认音色" style="width: 150px; margin-right: 10px;">
              <el-option
                v-for="(speaker, key) in availableSpeakers"
                :key="key"
                :label="speaker.name"
                :value="key"
              />
            </el-select>
            <el-button type="primary" @click="generateAllVoices" :loading="generatingAll">
              <el-icon><Microphone /></el-icon>
              批量生成配音
            </el-button>
          </div>
        </div>
      </template>

      <el-empty v-if="!safeScenes.length" description="暂无剧本数据">
        <p>请先在剧本拆解步骤生成剧本</p>
      </el-empty>

      <div v-else class="voice-content">
        <el-table :data="safeScenes" style="width: 100%" v-loading="loading">
          <el-table-column type="index" label="场景" width="80" />
          <el-table-column label="标题" width="150">
            <template #default="{ row }">
              {{ row.title || `场景${row.scene_number}` }}
            </template>
          </el-table-column>
          <el-table-column label="台词/旁白" min-width="250">
            <template #default="{ row }">
              <div v-if="row.dialogue && row.dialogue.length > 0">
                <div v-for="(line, idx) in row.dialogue.slice(0, 2)" :key="idx" class="dialogue-preview">
                  <span class="speaker">{{ line.speaker }}:</span>
                  <span class="content">{{ line.content }}</span>
                </div>
                <el-tag v-if="row.dialogue.length > 2" size="small" type="info">
                  +{{ row.dialogue.length - 2 }} 句
                </el-tag>
              </div>
              <div v-else-if="row.narration" class="narration-preview">
                {{ row.narration }}
              </div>
              <span v-else class="no-content">无台词</span>
            </template>
          </el-table-column>
          <el-table-column label="音色" width="120">
            <template #default="{ row }">
              <el-select v-if="hasVoiceContent(row)" v-model="row.speaker" size="small" style="width: 100px;">
                <el-option
                  v-for="(speaker, key) in availableSpeakers"
                  :key="key"
                  :label="speaker.name"
                  :value="key"
                />
              </el-select>
              <el-tag v-else type="info" size="small">-</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="120">
            <template #default="{ row }">
              <el-tag v-if="hasVoiceContent(row)" :type="getVoiceStatusType(row)" size="small">
                {{ getVoiceStatusLabel(row) }}
              </el-tag>
              <el-tag v-else type="info" size="small">无内容</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="时长" width="100">
            <template #default="{ row }">
              {{ row.voice_duration && !isNaN(row.voice_duration) ? `${Number(row.voice_duration).toFixed(1)}秒` : '-' }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200">
            <template #default="{ row }">
              <el-button
                v-if="row.voice_url"
                type="primary"
                size="small"
                @click="playAudio(row.voice_url)"
              >
                <el-icon><VideoPlay /></el-icon>
                试听
              </el-button>
              <el-button
                v-if="hasVoiceContent(row)"
                type="success"
                size="small"
                :loading="row.generating"
                @click="generateSceneVoice(row)"
              >
                <el-icon><Microphone /></el-icon>
                {{ row.voice_url ? '重新生成' : '生成' }}
              </el-button>
              <el-tag v-else type="info" size="small">无内容</el-tag>
            </template>
          </el-table-column>
        </el-table>

        <div class="voice-stats" v-if="voiceStats.total > 0">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-statistic title="总场景数" :value="voiceStats.total" />
            </el-col>
            <el-col :span="8">
              <el-statistic title="已生成" :value="voiceStats.generated" />
            </el-col>
            <el-col :span="8">
              <el-statistic title="总时长" :value="voiceStats.totalDuration.toFixed(1)" suffix="秒" />
            </el-col>
          </el-row>
        </div>

        <div class="actions">
          <el-button type="success" @click="confirmVoice" size="large" :disabled="voiceStats.generated === 0">
            <el-icon><Check /></el-icon>
            确认配音并继续
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 音频播放器 -->
    <audio ref="audioPlayer" style="display: none;"></audio>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { useProjectStore } from '../../stores/project'
import { getScripts, generateVoice, getVoices } from '../../api/project'

const props = defineProps({
  projectId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['step-complete', 'step-save'])

const projectStore = useProjectStore()
const scenes = ref([])
const loading = ref(false)
const generatingAll = ref(false)
const defaultSpeaker = ref('Cherry')
const audioPlayer = ref(null)

// 确保 scenes 始终是数组
const safeScenes = computed(() => scenes.value || [])

// 可用音色列表
const availableSpeakers = {
  'Cherry': { name: '芊悦', gender: '女', description: '阳光积极、亲切自然小姐姐' },
  'Serena': { name: '苏瑶', gender: '女', description: '温柔小姐姐' },
  'Ethan': { name: '晨煦', gender: '男', description: '标准普通话，阳光、温暖、活力、朝气' },
  'Chelsie': { name: '千雪', gender: '女', description: '二次元虚拟女友' },
  'Momo': { name: '茉兔', gender: '女', description: '撒娇搞怪，逗你开心' },
  'Vivian': { name: '十三', gender: '女', description: '拽拽的、可爱的小暴躁' },
  'Moon': { name: '月白', gender: '男', description: '率性帅气的月白' },
  'Maia': { name: '四月', gender: '女', description: '知性与温柔的碰撞' },
  'Kai': { name: '凯', gender: '男', description: '耳朵的一场SPA' },
  'Eldric Sage': { name: '沧明子', gender: '男', description: '沉稳睿智的老者' },
  'Mochi': { name: '沙小弥', gender: '男', description: '聪明伶俐的小大人' },
  'Mia': { name: '乖小妹', gender: '女', description: '温顺如春水，乖巧如初雪' }
}

// 统计信息
const voiceStats = computed(() => {
  const scenesList = safeScenes.value
  if (!scenesList || !Array.isArray(scenesList)) {
    return { total: 0, generated: 0, totalDuration: 0 }
  }
  const total = scenesList.length
  const generated = scenesList.filter(s => s && s.voice_url).length
  const totalDuration = scenesList.reduce((sum, s) => sum + (s && s.voice_duration ? Number(s.voice_duration) : 0), 0)
  return { total, generated, totalDuration }
})

// 加载剧本和语音数据
onMounted(async () => {
  await loadScenesAndVoices()
})

const loadScenesAndVoices = async () => {
  loading.value = true
  try {
    // 加载剧本场景
    const scriptsResponse = await getScripts(props.projectId)
    
    // 加载已生成的语音
    const voicesResponse = await getVoices(props.projectId)
    
    // 创建语音查找表（按 script_id）
    const voiceMap = {}
    if (voicesResponse && Array.isArray(voicesResponse)) {
      voicesResponse.forEach(voice => {
        if (voice.script_id) {
          voiceMap[voice.script_id] = voice
        }
      })
    }

    if (scriptsResponse && scriptsResponse.length > 0) {
      scenes.value = scriptsResponse.map((script, index) => {
        const voice = voiceMap[script.id] || {}
        
        return {
          id: script.id,
          scene_number: index + 1,
          title: script.scene_title || '',
          description: script.scene_description || '',
          narration: script.narration || '',
          dialogue: script.dialogue || [],
          characters: script.characters_present || [],
          location: script.location || '',
          // 语音相关字段
          voice_id: voice.id || null,
          voice_url: voice.mp3_url || '',
          voice_duration: voice.duration || 0,
          speaker: voice.speaker || defaultSpeaker.value,
          generatings: false
        }
      })
    }
  } catch (error) {
    console.error('Load scenes and voices error:', error)
    ElMessage.error('加载数据失败')
  } finally {
    loading.value = false
  }
}

// 获取语音状态类型
const getVoiceStatusType = (row) => {
  if (row.generating) return 'warning'
  if (row.voice_url) return 'success'
  return 'info'
}

// 获取语音状态标签
const getVoiceStatusLabel = (row) => {
  if (row.generating) return '生成中'
  if (row.voice_url) return '已生成'
  return '待生成'
}

// 生成单个场景的语音
const generateSceneVoice = async (row) => {
  if (!row.id) {
    ElMessage.warning('场景ID不存在')
    return
  }

  // 准备文本内容 - 只使用台词或旁白，不使用场景描述
  let text = ''
  if (row.dialogue && row.dialogue.length > 0) {
    // 使用台词 - 只拼接内容，不添加"角色名说："
    text = row.dialogue.map(d => d.content).join('。')
  } else if (row.narration) {
    // 使用旁白
    text = row.narration
  }

  if (!text) {
    ElMessage.warning('该场景没有可合成的文本内容（需要有台词或旁白）')
    return
  }

  row.generating = true
  try {
    const response = await generateVoice(row.id, row.speaker, text)
    
    if (response && response.voice_url) {
      row.voice_url = response.voice_url
      row.voice_duration = response.duration || 0
      row.voice_id = response.voice_id
      ElMessage.success(`场景${row.scene_number}语音生成成功`)
    } else {
      ElMessage.warning('语音生成失败，请重试')
    }
  } catch (error) {
    console.error('Generate voice error:', error)
    ElMessage.error('语音生成失败：' + (error.message || '未知错误'))
  } finally {
    row.generating = false
  }
}

// 批量生成所有语音
const generateAllVoices = async () => {
  const pendingScenes = scenes.value.filter(s => !s.voice_url && !s.generating)
  
  if (pendingScenes.length === 0) {
    ElMessage.info('所有场景都已生成语音')
    return
  }

  generatingAll.value = true
  let successCount = 0
  let failCount = 0

  for (const row of pendingScenes) {
    try {
      await generateSceneVoice(row)
      if (row.voice_url) {
        successCount++
      } else {
        failCount++
      }
    } catch (error) {
      failCount++
      console.error(`Generate voice for scene ${row.scene_number} failed:`, error)
    }
    
    // 添加小延迟，避免请求过快
    await new Promise(resolve => setTimeout(resolve, 500))
  }

  generatingAll.value = false
  
  if (successCount > 0) {
    ElMessage.success(`成功生成 ${successCount} 个场景的语音`)
  }
  if (failCount > 0) {
    ElMessage.warning(`${failCount} 个场景生成失败`)
  }
}

// 播放音频
const playAudio = (url) => {
  if (audioPlayer.value) {
    audioPlayer.value.src = url
    audioPlayer.value.play()
  }
}

// 检查场景是否有可合成的内容
const hasVoiceContent = (row) => {
  return (row.dialogue && row.dialogue.length > 0) || (row.narration && row.narration.trim())
}

// 确认配音
const confirmVoice = async () => {
  if (voiceStats.value.generated === 0) {
    ElMessage.warning('请至少生成一个场景的语音')
    return
  }

  try {
    // 更新项目状态为语音合成完成
    await projectStore.updateProjectStatus('voice_generated')
    ElMessage.success('配音确认成功')
    emit('step-complete')
  } catch (error) {
    console.error('Confirm voice error:', error)
    ElMessage.error('状态更新失败')
  }
}
</script>

<style scoped lang="scss">
.voice-step {
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header-actions {
    display: flex;
    align-items: center;
  }
}

.dialogue-preview {
  margin: 4px 0;
  font-size: 13px;
  
  .speaker {
    font-weight: 600;
    color: #409eff;
    margin-right: 4px;
  }
  
  .content {
    color: #606266;
  }
}

.narration-preview {
  color: #606266;
  font-size: 13px;
  line-height: 1.5;
}

.no-content {
  color: #909399;
  font-style: italic;
}

.voice-stats {
  margin-top: 24px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 4px;
}

.actions {
  margin-top: 24px;
  text-align: center;
}
</style>
